import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";

import {
    Box,
    Button,
    CircularProgress,
    Checkbox,
    TextField,
    Typography,
    Chip,
    Avatar,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    Switch,
    FormControlLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SaveIcon from "@material-ui/icons/Save";
import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from "@material-ui/icons/Visibility";

import { useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPatch, getConfigPost } from "../getConfig";

const SHOP_GRID = API + "/admin/shop-grid";

interface Category {
    id: number;
    category: string;
    display_name: string;
}

interface OtherBrand {
    brand: string;
    type: string;
    placement_id: number | null;
    disabled: boolean;
    hidden: boolean;
}

interface GridRow {
    brand: string;
    logo: string | null;
    type: string;
    disabled: boolean;
    placement_id: number | null;
    pin_type: "pin" | "hidden" | null;
    pin_position: number | null;
    new_from: string | null;
    reward: string | null;
    is_guaranteed: boolean;
    pinned_in_other_category: number | null;
    uk_spend: number | null;
    sprive_sales: number | null;
    // local edit state — a non-empty _pinPos means the brand is pinned at that slot.
    _hidden: boolean;
    _pinPos: string;
    _new: boolean;
    _saved: string;
}

const useStyles = makeStyles((theme) => ({
    panel: {
        padding: theme.spacing(2),
        marginLeft: theme.spacing(6),
        backgroundColor: "#fafafa",
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        borderRadius: "0 6px 6px 0",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(1),
    },
    logo: { width: 28, height: 28 },
    posInput: { width: 60 },
    dirty: { backgroundColor: "#fff8e1" },
    disabledRow: { opacity: 0.5 },
    typeChip: { textTransform: "uppercase", fontSize: 10 },
    addBox: { marginTop: theme.spacing(2) },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: theme.spacing(2),
    },
    card: {
        border: "1px solid #ececec",
        borderRadius: 12,
        background: "#fff",
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        gap: theme.spacing(2),
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    },
    cardDirty: { boxShadow: "0 0 0 2px #ffb300" },
    cardDisabled: { opacity: 0.5 },
    cardHidden: { background: "#f2f2f2", borderStyle: "dashed" },
    cardLeft: {
        width: 72,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(0.75),
    },
    cardRight: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: theme.spacing(1.25),
        minWidth: 0,
        paddingLeft: theme.spacing(3),
        borderLeft: "1px solid #eee",
    },
    logoBox: {
        height: 44,
        width: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    logoImg: { maxHeight: 40, maxWidth: "100%", objectFit: "contain" as const },
    name: { fontWeight: 600, fontSize: 12.5, textAlign: "center" as const, lineHeight: 1.25 },
    flags: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },
    metrics: {
        display: "flex",
        flexWrap: "wrap",
        fontSize: 11,
        color: "#666",
        "& > span:not(:last-child)": { marginRight: theme.spacing(2.5) },
    },
    metricValue: { fontWeight: 600, color: "#333" },
    legend: { color: "#777", marginBottom: theme.spacing(1) },
    controls: {
        display: "inline-flex",
        alignSelf: "flex-start",
        maxWidth: "100%",
        alignItems: "center",
        gap: theme.spacing(2),
        flexWrap: "wrap",
        background: "#f7f7f9",
        borderRadius: 8,
        padding: theme.spacing(0.75, 1.25),
    },
    ctrlLabel: { margin: 0 },
    ctrlText: { fontSize: 11, color: "#555" },
    dialogSearch: { marginBottom: theme.spacing(1) },
    previewBar: {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
        padding: theme.spacing(0, 1),
        borderRadius: 6,
        marginBottom: theme.spacing(1),
        border: "1px solid",
    },
    previewOn: {
        backgroundColor: "#e8f4fd",
        borderColor: "#2196f3",
        color: "#0d47a1",
    },
    previewOff: {
        backgroundColor: "#f4f4f4",
        borderColor: "#9e9e9e",
        color: "#555",
    },
}));

// Compact GBP: £1.2m / £45k / £320 / — when unset.
function fmtGBP(n: number | null): string {
    if (n == null) return "—";
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}m`;
    if (abs >= 1_000) return `£${(n / 1_000).toFixed(1)}k`;
    return `£${n}`;
}

function desiredOf(r: GridRow, categoryId: number) {
    // 0 / blank / non-numeric = not pinned → clear the whole pin.
    const pos = Number(r._pinPos);
    const hasPin = !r._hidden && r._pinPos.trim() !== "" && Number.isFinite(pos) && pos > 0;
    return {
        pin_type: r._hidden ? "hidden" : hasPin ? "pin" : null,
        pin_position: hasPin ? pos : null,
        pin_category_id: r._hidden || hasPin ? categoryId : null,
        new_from: r._new ? r.new_from || new Date().toISOString().slice(0, 10) : null,
    };
}

const CategoryBrandPanel = ({ category }: { category: Category }) => {
    const classes = useStyles();
    const notify = useNotify();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState<GridRow[]>([]);
    const [others, setOthers] = useState<OtherBrand[]>([]);
    const [addOpen, setAddOpen] = useState(false);
    const [addSearch, setAddSearch] = useState("");
    const [hiddenOnly, setHiddenOnly] = useState(false);
    const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
    const [vdxEnabled, setVdxEnabled] = useState(true);

    useEffect(() => {
        fetchBrands();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category.id, vdxEnabled]);

    const toGridRow = (b: any): GridRow => {
        const row: GridRow = {
            ...b,
            _hidden: b.pin_type === "hidden",
            _pinPos: b.pin_position != null ? String(b.pin_position) : "",
            _new: !!b.new_from,
            _saved: "",
        };
        row._saved = JSON.stringify(desiredOf(row, category.id));
        return row;
    };

    const fetchBrands = async () => {
        try {
            setLoading(true);
            const res: any = await axios.get(
                SHOP_GRID,
                getConfigGet({ resource: "brands", category: category.category, vdxEnabled })
            );
            const data = res.data || {};
            setRows((data.grid || []).map(toGridRow));
            setOthers(data.others || []);
        } catch (e) {
            notify("Failed to load brands for this category", "warning");
        } finally {
            setLoading(false);
        }
    };

    const update = (index: number, patch: Partial<GridRow>) => {
        setRows((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], ...patch };
            return next;
        });
    };

    // Entering a position pins the brand (and clears hide). Hiding clears the position.
    const setPos = (i: number, value: string) =>
        update(i, { _pinPos: value, _hidden: value.trim() !== "" ? false : rows[i]._hidden });
    const toggleHide = (i: number, on: boolean) =>
        update(i, { _hidden: on, _pinPos: on ? "" : rows[i]._pinPos });

    const unhideBrand = async (b: OtherBrand) => {
        if (!b.placement_id) return;
        try {
            await axios.patch(
                `${SHOP_GRID}/${b.placement_id}?resource=placement`,
                { pin_type: null, pin_position: null, pin_category_id: null },
                { headers: getConfigPatch() }
            );
            notify(`Unhid ${b.brand} — will show on the grid if eligible`, "info");
            setAddOpen(false);
            fetchBrands();
        } catch (e: any) {
            notify(e?.response?.data?.message || "Failed to unhide", "error");
        }
    };

    const addBrand = (b: OtherBrand) => {
        if (b.hidden) {
            unhideBrand(b);
            return;
        }
        const nextPos = rows.filter((r) => r._pinPos.trim() !== "").length + 1;
        const newRow: GridRow = {
            brand: b.brand,
            logo: null,
            type: b.type,
            disabled: b.disabled,
            placement_id: b.placement_id,
            pin_type: null,
            pin_position: null,
            new_from: null,
            reward: null,
            is_guaranteed: false,
            pinned_in_other_category: null,
            uk_spend: null,
            sprive_sales: null,
            _hidden: false,
            _pinPos: String(nextPos),
            _new: false,
            // saved = its pre-add (unplaced) state, so the new pin shows as dirty
            _saved: JSON.stringify({
                pin_type: null,
                pin_position: null,
                pin_category_id: null,
                new_from: null,
            }),
        };
        setRows((prev) => [...prev, newRow]);
        setOthers((prev) => prev.filter((o) => !(o.brand === b.brand && o.type === b.type)));
        setAddOpen(false);
        setAddSearch("");
    };

    const isDirty = (r: GridRow) => JSON.stringify(desiredOf(r, category.id)) !== r._saved;
    const dirtyCount = rows.filter(isDirty).length;

    const handleSave = async () => {
        setSaving(true);
        try {
            const changed = rows.filter(isDirty);
            for (const r of changed) {
                const desired = desiredOf(r, category.id);
                if (r.placement_id) {
                    await axios.patch(`${SHOP_GRID}/${r.placement_id}?resource=placement`, desired, {
                        headers: getConfigPatch(),
                    });
                } else {
                    await axios.post(
                        SHOP_GRID,
                        { resource: "placement", brand: r.brand, ...desired },
                        { headers: getConfigPost() }
                    );
                }
            }
            notify(changed.length ? `Saved ${changed.length} brand(s)` : "No changes", "info");
            fetchBrands();
        } catch (e: any) {
            notify(e?.response?.data?.message || "Failed to save", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    const hiddenCount = others.filter((o) => o.hidden).length;
    const giftCardCount = others.filter((o) => o.type === "giftCard").length;
    const vdxCount = others.filter((o) => o.type === "vdx").length;
    const toggleType = (t: string, on: boolean) =>
        setTypeFilter((prev) => {
            const next = new Set(prev);
            if (on) next.add(t);
            else next.delete(t);
            return next;
        });
    const filteredOthers = others.filter(
        (o) =>
            o.brand.toLowerCase().includes(addSearch.toLowerCase()) &&
            (!hiddenOnly || o.hidden) &&
            (typeFilter.size === 0 || typeFilter.has(o.type))
    );

    return (
        <div className={classes.panel}>
            <div
                className={`${classes.previewBar} ${
                    vdxEnabled ? classes.previewOn : classes.previewOff
                }`}
            >
                <VisibilityIcon style={{ fontSize: 16 }} />
                <Typography variant="caption" style={{ fontWeight: 600 }}>
                    Previewing {vdxEnabled ? "VDX-enabled" : "VDX-disabled"} users
                </Typography>
                <Switch
                    size="small"
                    color="primary"
                    checked={vdxEnabled}
                    onChange={(e) => setVdxEnabled(e.target.checked)}
                />
            </div>

            <div className={classes.header}>
                <Typography variant="subtitle2">
                    On the grid — {category.display_name} ({rows.length})
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                    disabled={saving || dirtyCount === 0}
                    onClick={handleSave}
                >
                    {saving ? "Saving..." : `Save${dirtyCount ? ` (${dirtyCount})` : ""}`}
                </Button>
            </div>

            <Typography variant="caption" className={classes.legend} component="div">
                All cards are on the grid. <b>filler</b> = fills leftover slots after
                pinned / high-spend brands.
            </Typography>

            <div className={classes.grid}>
                {rows.map((r, i) => (
                    <div
                        key={r.type + r.brand + i}
                        className={[
                            classes.card,
                            isDirty(r) ? classes.cardDirty : "",
                            r.disabled ? classes.cardDisabled : "",
                            r._hidden ? classes.cardHidden : "",
                        ].join(" ").trim()}
                    >
                        <div className={classes.cardLeft}>
                            <div className={classes.logoBox}>
                                {r.logo ? (
                                    <img src={r.logo} alt={r.brand} className={classes.logoImg} />
                                ) : (
                                    <Avatar variant="rounded">{r.brand?.[0]}</Avatar>
                                )}
                            </div>
                            <div className={classes.name}>{r.brand}</div>
                        </div>

                        <div className={classes.cardRight}>
                            <div className={classes.flags}>
                                <Chip
                                    size="small"
                                    className={classes.typeChip}
                                    variant="outlined"
                                    label={r.type}
                                />
                                {r.reward && (
                                    <Chip size="small" variant="outlined" label={r.reward} />
                                )}
                                {!r.is_guaranteed && (
                                    <Tooltip title="Only fills leftover slots after guaranteed brands">
                                        <Chip size="small" label="filler" />
                                    </Tooltip>
                                )}
                                {r.pinned_in_other_category && (
                                    <Tooltip title={`Also pinned in category #${r.pinned_in_other_category}`}>
                                        <Chip size="small" label="pinned elsewhere" />
                                    </Tooltip>
                                )}
                                {r.disabled && (
                                    <Chip size="small" color="secondary" label="disabled" />
                                )}
                            </div>
                            <div className={classes.metrics}>
                                <span>
                                    UK 90d{" "}
                                    <span className={classes.metricValue}>{fmtGBP(r.uk_spend)}</span>
                                </span>
                                <span>
                                    Sprive 30d{" "}
                                    <span className={classes.metricValue}>{fmtGBP(r.sprive_sales)}</span>
                                </span>
                            </div>
                            <div className={classes.controls}>
                                <TextField
                                    className={classes.posInput}
                                    size="small"
                                    type="number"
                                    label="Pin"
                                    placeholder="—"
                                    disabled={r._hidden}
                                    value={r._pinPos}
                                    onChange={(e) => setPos(i, e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <FormControlLabel
                                    className={classes.ctrlLabel}
                                    control={<Checkbox size="small" checked={r._hidden} onChange={(e) => toggleHide(i, e.target.checked)} />}
                                    label={<span className={classes.ctrlText}>Hide</span>}
                                />
                                <FormControlLabel
                                    className={classes.ctrlLabel}
                                    control={<Checkbox size="small" checked={r._new} onChange={(e) => update(i, { _new: e.target.checked })} />}
                                    label={<span className={classes.ctrlText}>New</span>}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Box className={classes.addBox}>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                    Add brand ({others.length})
                </Button>
            </Box>

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Add brand to {category.display_name}</DialogTitle>
                <DialogContent>
                    <TextField
                        className={classes.dialogSearch}
                        fullWidth
                        size="small"
                        placeholder="Search brands..."
                        value={addSearch}
                        onChange={(e) => setAddSearch(e.target.value)}
                        autoFocus
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={hiddenOnly}
                                onChange={(e) => setHiddenOnly(e.target.checked)}
                            />
                        }
                        label={<Typography variant="caption">Hidden only ({hiddenCount})</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={typeFilter.has("giftCard")}
                                onChange={(e) => toggleType("giftCard", e.target.checked)}
                            />
                        }
                        label={<Typography variant="caption">Gift card ({giftCardCount})</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={typeFilter.has("vdx")}
                                onChange={(e) => toggleType("vdx", e.target.checked)}
                            />
                        }
                        label={<Typography variant="caption">VDX ({vdxCount})</Typography>}
                    />
                    <List dense style={{ maxHeight: 360, overflow: "auto" }}>
                        {filteredOthers.map((o) => (
                            <ListItem button key={o.type + o.brand} onClick={() => addBrand(o)}>
                                <ListItemText primary={o.brand} secondary={o.type} />
                                {o.hidden ? (
                                    <Chip size="small" color="primary" label="unhide" />
                                ) : (
                                    <Chip size="small" variant="outlined" label="add + pin" />
                                )}
                            </ListItem>
                        ))}
                        {filteredOthers.length === 0 && (
                            <ListItem>
                                <ListItemText primary="No brands found" />
                            </ListItem>
                        )}
                    </List>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CategoryBrandPanel;
